import {
  BAR_PULSE,
  BAR_PULSE_KIND,
  BAR_PULSE_ORIGIN,
  defaultParams,
  isAlive,
  KIND_INDEX,
  type Disturbance,
  type DisturbanceKind,
  type DisturbanceParams,
} from './disturbance';

/**
 * **标量场控制器（CPU 侧）** —— 决定"什么时候往场里扔什么"。
 *
 * 场的**数值**全部在 GPU 上算；这里只生产/回收扰动的**参数**，
 * 并把还活着的那些打包成 uniform 数组。
 *
 * ## 为什么这一层要单独存在、还要能测
 *
 * 因为它是最容易出错、又最难靠肉眼定位的一层：
 * 寿命算错 → 波纹永远不消失（或一出生就没了）；容量溢出 → 新波纹悄悄丢失；
 * 打包错位 → 场上出现一个参数完全错乱的图案。
 * 这些在屏幕上全都表现为"看起来不太对"，查起来毫无线索。
 *
 * ⚠️ **这里用 `Math.random`，不用 `core/rng` 的种子化 PRNG。**
 *
 * 规矩见 `docs/onboarding.md` §4.4 第 5 条：**纯视效的随机不需要可复现** ——
 * 一圈涟漪这次从哪儿冒出来、裂缝这条臂偏几度，对玩家和测试都没有意义；
 * 种子化随机是留给**逻辑**的（棋谱、发牌、机器人决策、房间号）。
 *
 * （原来这里有个 `seed` 选项 + `nextU32`：图的是一串扰动可以在测试里断言。
 *  实测没人靠它，倒是每次"加一个纯视效随机"都要先想一遍种子该给谁。）
 */

/**
 * 同时最多有多少条扰动在场。
 *
 * 12 条足够撑起"水面上同时有好几圈涟漪"的画面，而 uniform 占用很小
 * （3 个 `vec4` × 12 = 36 个 vec4）。**超出容量的新扰动会被丢掉**，
 * 不是覆盖旧的 —— 覆盖会让已经画出来的波纹突然变形。
 */
export const MAX_DISTURBANCES = 12;

/** 打包好的 uniform 载荷。三个数组一一对应着色器里的 `uDistA / uDistB / uDistC` */
export interface DisturbancePack {
  /**
   * 实际有效的条数。
   *
   * ⚠️ 这个对象是**复用**的（每帧原地覆写，避免渲染循环里分配内存），
   * 所以 `count` 可变、数组也是同一批。调用方拿到就要用完，
   * 不要存起来等下一帧 —— 下一帧它就被改了。
   */
  count: number;
  /** `(kindIndex, x, y, bornAt)` */
  readonly a: Float32Array;
  /** `(amp, freq, speed, life)` */
  readonly b: Float32Array;
  /** `(radius, phase, 0, 0)` */
  readonly c: Float32Array;
}

export class ScalarField {
  readonly #active: Disturbance[] = [];

  /** 三个数组预先分配好，每帧原地覆写 —— 渲染循环里不该有分配 */
  readonly #pack: DisturbancePack = {
    count: 0,
    a: new Float32Array(MAX_DISTURBANCES * 4),
    b: new Float32Array(MAX_DISTURBANCES * 4),
    c: new Float32Array(MAX_DISTURBANCES * 4),
  };

  /**
   * ⚠️ **没有构造参数了。** 原来这里有一个 `seed`（让扰动的初相可复现），
   * 评审第六轮定下"纯视效的随机不需要可复现"之后去掉了 —— 留着一个没人用的
   * `seed` 只会让人以为这一层是可复现的（见 `docs/onboarding.md` §4.4 第 5 条）。
   */
  constructor() {}

  /** 现在场上还活着几条 */
  get count(): number {
    return this.#active.length;
  }

  get active(): readonly Disturbance[] {
    return this.#active;
  }

  /**
   * 手动扔一条（鼠标点击就是走这里）。
   *
   * 返回 `null` 表示**容量满了，这条被丢掉了** —— 调用方不必处理，
   * 但测试会盯着这个语义。
   */
  spawn(
    kind: DisturbanceKind,
    x: number,
    y: number,
    now: number,
    overrides: Partial<DisturbanceParams> = {},
  ): Disturbance | null {
    if (this.#active.length >= MAX_DISTURBANCES) return null;

    const base = defaultParams(kind);
    const disturbance: Disturbance = {
      kind,
      x: clamp01(x),
      y: clamp01(y),
      bornAt: now,
      amp: overrides.amp ?? base.amp,
      freq: overrides.freq ?? base.freq,
      speed: overrides.speed ?? base.speed,
      life: overrides.life ?? base.life,
      radius: overrides.radius ?? base.radius,
      phase: overrides.phase ?? this.#float() * Math.PI * 2,
    };

    this.#active.push(disturbance);
    return disturbance;
  }

  /**
   * **放一发 Bar 脉冲** —— 从画面正中向外推的一圈波。
   *
   * 主界面里每小节来一次（`ui/beat.ts` 的 Bar 事件 → `Background.pulseFromCenter`）。
   * 收在这里而不是写在 `Background` 里，是因为"一发脉冲长什么样"属于扰动本身
   * （和 `spawnRandom` 同一层），而 `Background` 只是把它接到渲染管线上；
   * 顺带这一层不 import Pixi，于是"位置对不对、强度怎么缩放"能在 node 里断言。
   *
   * ⚠️ 位置写死 `(0.5, 0.5)`，`phase` 显式 0。两点都是**为了可复现**：
   * 拍子上的东西每次必须一模一样，随机的相位会让"每一圈从不同的形状开始"。
   *
   * ## `fieldRate` 是"场时间 / 墙上时间"
   *
   * `BAR_PULSE` 里的 `speed` / `life` 是按**墙上时间**定的（一小节扫到边角），
   * 而着色器里的 `age` 走的是**场时间**（`Background` 的时间倍率现在只有 0.1）。
   * 两个不是一回事，所以这里按倍率换算：
   *
   * ```
   *   speed_场 = speed_墙上 / rate     // 一个场秒里要走完"墙上 rate 秒"的距离
   *   life_场  = life_墙上  * rate     // 墙上 1.35 秒 = 场里 0.135 秒
   * ```
   *
   * ⚠️ 两个的方向**相反**（速度除、寿命乘），因为"率"在它们身上的作用不同：
   * 时间变慢 → 同样一段路要走得更快、同样一段墙上时间在场里更短。
   * 一个都不换、或者两个都除，后果是"跟着小节的脉冲"变成一百多秒才淌出去
   * 一圈（而且它一直占着扰动槽位）—— 而它不会报错，只是不再和音乐有关系。
   */
  spawnBarPulse(now: number, strength = 1, fieldRate = 1): Disturbance | null {
    const rate = fieldRate > 0 && Number.isFinite(fieldRate) ? fieldRate : 1;
    return this.spawn(BAR_PULSE_KIND, BAR_PULSE_ORIGIN, BAR_PULSE_ORIGIN, now, {
      ...BAR_PULSE,
      amp: BAR_PULSE.amp * strength,
      speed: BAR_PULSE.speed / rate,
      life: BAR_PULSE.life * rate,
    });
  }

  /**
   * 每帧推进一步：**只回收过期的**。
   *
   * ⚠️ 这里**不再按节奏自动撒扰动**（评审：「取消目前所有随机扰动逻辑，只保留
   * 点击、濒死以及其他原因的事件扰动」）。背景的"底"由标量场的初始状态
   * （alexharri 那层，见 `alexharri.ts`）负责，它每一帧都在动 ——
   * 所以没有扰动的时候画面不是死的，而扰动只在**真的发生了什么**的时候出现。
   *
   * ⚠️ 回收用的是 `isAlive(d, now)`，**和着色器里的时间包络同一个判据**。
   * 两边不一致的话，会出现"还在场上但已经看不见"（白占容量）
   * 或者"看得见但已经被丢掉"（波纹半路消失）。
   */
  tick(_dt: number, now: number): void {
    for (let i = this.#active.length - 1; i >= 0; i--) {
      const d = this.#active[i];
      if (d !== undefined && !isAlive(d, now)) this.#active.splice(i, 1);
    }
  }

  clear(): void {
    this.#active.length = 0;
  }

  /** 打包成 uniform 载荷。每帧调一次，原地覆写，不分配 */
  pack(now: number): DisturbancePack {
    const { a, b, c } = this.#pack;
    a.fill(0);
    b.fill(0);
    c.fill(0);

    let n = 0;
    for (const d of this.#active) {
      if (!isAlive(d, now)) continue;
      if (n >= MAX_DISTURBANCES) break;

      a[n * 4 + 0] = KIND_INDEX[d.kind];
      a[n * 4 + 1] = d.x;
      a[n * 4 + 2] = d.y;
      a[n * 4 + 3] = d.bornAt;

      b[n * 4 + 0] = d.amp;
      b[n * 4 + 1] = d.freq;
      b[n * 4 + 2] = d.speed;
      b[n * 4 + 3] = d.life;

      c[n * 4 + 0] = d.radius;
      c[n * 4 + 1] = d.phase;
      n++;
    }

    this.#pack.count = n;
    return this.#pack;
  }

  // ---- 随机（纯视效，见文件头）----

  /** [0, 1)。**`Math.random`**：这一层的随机不需要可复现 */
  #float(): number {
    return Math.random();
  }

}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
